// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { AddressFieldDisplayCollectionModel, BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { classToVariant, OrderStatusQueryString } from "@vanmeter/XDL/constants";
import xIconsObject from "@vanmeter/XDL/Icons/xngCommonIcons";
import systemZIndex from "@vanmeter/XDL/PreStyle/zIndex";
import { CustomLinkModel, CustomPageLinkModel } from "@vanmeter/XDL/types";
import { DomUtils, parseDocument } from "htmlparser2";
import isDate from "lodash/isDate";

/**
 * To load JS script with callback.
 * you can pass one url or multiple
 * Example:
 * getScripts.urls([ // url1, url2]).then(() => { //callback handler });
 */
export const getScripts = {
    url: (url: string) => {
        return new Promise((resolve, reject) => {
            if (typeof document !== "undefined") {
                const script = document.createElement("script");
                script.type = "text/javascript";
                script.src = url;
                script.addEventListener("load", () => resolve(script), false);
                script.addEventListener("error", () => reject(script), false);
                document.body.appendChild(script);
            }
        });
    },
    urls: (urls: string[]) => {
        return Promise.all(urls.map(getScripts.url));
    },
};

export const isSmallMobileScreen = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 480px)").matches;

export const isMobileScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

export const isTabletScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches;

export const isWideScreen = () => typeof window !== "undefined" && window.matchMedia("(min-width: 2200px)").matches;

/**
 * To convert string to boolean
 * @param value
 */
export const parseBoolean = (value?: string) => (value ? value?.toLowerCase() === "true" : false);

/**
 * split without empty or undefined values
 * @param value
 */
export const flatSplit = (value?: string, target = "") => (value || "").split(target).filter(item => item) || [];

/**
 * Method use to pass for props not accept undefined
 */
export const doNothing = () => {};

/**
 * Helper function to export data to csv file
 * @param fields
 * @param data
 * @param filename
 */
export const exportToCSV = async (fields: string[], data: (string | number | null)[][], filename?: string) => {
    const dataForUnparse = {
        fields,
        data,
    };

    const { unparse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");
    const csv = unparse(dataForUnparse);
    const csvBlob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const fileName = `${filename || "csv_exported_file"}_${Math.random() * 10000}.csv`;
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(csvBlob);
    downloadLink.setAttribute("download", fileName);
    downloadLink.click();
};

export const getSystemZIndex = (key: string): number => {
    const zIndexValues = systemZIndex as any;
    return zIndexValues[key] || -1;
};

export const formatDate = (date: Date | null) => {
    if (!date) {
        return "";
    }

    let dt = date;
    if (!isDate(dt)) {
        dt = new Date(dt);
    }

    return getLocalizedDateTime({
        dateTime: dt,
        options: {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        },
    });
};

export const getVideoEmbeddedUrl = (src: string) => {
    if (!src) {
        return;
    }
    if (src.includes("vimeo")) {
        return src;
    }
    const srcParts = src.split("/");
    const videoId = srcParts.pop();
    return `https://www.youtube.com/embed/${videoId}`;
};

export const getMyAccountMapLinks = (state: ApplicationState, widgetLinks: CustomLinkModel[]) => {
    const myAccountPage = getPageLinkByPageType(state, "MyAccountPage");
    return (
        widgetLinks
            ?.map(widgetLink => {
                if (widgetLink.fields.isBillTrustLink) {
                    return {
                        url: "",
                        id: "",
                        title: widgetLink.fields.overriddenTitle,
                        isBillTrustLink: true,
                    };
                }
                if (widgetLink.fields.destination.type === "Page") {
                    const pageLink = getPageLinkByNodeId(state, widgetLink.fields.destination.value);
                    if (pageLink) {
                        return {
                            ...pageLink,
                            title: widgetLink.fields.overriddenTitle || pageLink?.title,
                            openInNewWindow: widgetLink.fields.openInNewWindow,
                            queryString: widgetLink.fields.queryString,
                        };
                    }
                } else if (widgetLink.fields.destination.type === "Url") {
                    const url = widgetLink.fields.destination.value;
                    const title = widgetLink.fields.overriddenTitle;

                    return { id: "", url, title, queryString: widgetLink.fields.queryString };
                }
                return undefined;
            })
            ?.filter(pageLink =>
                (pageLink as any)?.parentId === myAccountPage?.id ? !(pageLink as any)?.excludeFromNavigation : true,
            ) || []
    );
};

export const safeJsonParse = (str: string) => {
    try {
        return JSON.parse(str);
    } catch {
        return str;
    }
};

export const getIcon = (icon: string, size: number): IconPresentationProps | undefined => {
    const src = xIconsObject[icon];
    return src ? { src, size } : undefined;
};
export const extractParagraphStyle = (classNames: string | undefined) => {
    if (!classNames) {
        return { variant: undefined, className: "" };
    }

    const classList = classNames.split(" ");
    const matchIndex = classList.findIndex(cls => cls in classToVariant);

    if (matchIndex === -1) return { variant: undefined, className: classNames };

    const variant = classToVariant[classList[matchIndex]];
    classList.splice(matchIndex, 1);

    return { variant, className: classList.join(" ").trim() };
};

type ButtonVariant = "primary" | "secondary" | "tertiary" | "link";
type ButtonType = "solid" | "outline";

export function parseButtonVariant(rawValue: string = ""): { variant: ButtonVariant; buttonType: ButtonType } {
    const value = rawValue.trim();

    if (value === "link") {
        return {
            variant: "link",
            buttonType: "outline",
        };
    }

    const [variant, type] = value.split("-");

    const typedVariant = variant as ButtonVariant;

    const buttonType: ButtonType = type === "outline" ? "outline" : "solid";

    return {
        variant: typedVariant,
        buttonType,
    };
}

const VOID_ELEMENTS = new Set([
    "area",
    "base",
    "br",
    "col",
    "embed",
    "hr",
    "img",
    "input",
    "link",
    "meta",
    "param",
    "source",
    "track",
    "wbr",
]);

const truncateHtmlChars = (html: string, maxChars: number): { result: string; truncated: boolean } => {
    let charCount = 0;
    let i = 0;
    let result = "";
    const openStack: string[] = [];

    while (i < html.length) {
        if (charCount >= maxChars) break;
        if (html[i] === "<") {
            const end = html.indexOf(">", i);
            if (end === -1) {
                result += html.slice(i);
                break;
            }
            const inner = html.slice(i + 1, end).trim();
            const isClose = inner.startsWith("/");
            const isSelfClose = inner.endsWith("/");
            const tagName = (isClose ? inner.slice(1) : inner.split(/[\s/]/)[0]).toLowerCase();
            result += html.slice(i, end + 1);
            if (!isClose && !isSelfClose && !VOID_ELEMENTS.has(tagName)) {
                openStack.push(tagName);
            } else if (isClose) {
                const idx = openStack.lastIndexOf(tagName);
                if (idx !== -1) openStack.splice(idx, 1);
            }
            i = end + 1;
        } else if (html[i] === "&") {
            // Count HTML entity (&nbsp; &amp; &#8203; etc.) as 1 character
            const semi = html.indexOf(";", i + 1);
            const nextTag = html.indexOf("<", i + 1);
            const isEntity = semi !== -1 && (nextTag === -1 || semi < nextTag) && semi - i <= 32;
            if (isEntity) {
                result += html.slice(i, semi + 1);
                i = semi + 1;
            } else {
                result += html[i];
                i++;
            }
            charCount++;
        } else {
            result += html[i];
            charCount++;
            i++;
        }
    }

    for (let j = openStack.length - 1; j >= 0; j--) {
        result += `</${openStack[j]}>`;
    }

    return { result, truncated: charCount >= maxChars };
};

export const truncateWithoutWordBreak = (html: string, maxChars: number, showMoreLink: boolean) => {
    if (!html) {
        return "";
    }

    const { result, truncated } = truncateHtmlChars(html, maxChars);
    if (!truncated) {
        return html;
    }

    const cleaned = result.replace(/\b\S*$/, "");

    return cleaned + `...` + (showMoreLink ? ` <a data-action="view-more">${translate("View more")}</a>` : "");
};
export const stripHtml = (html: string): string => {
    if (!html) {
        return "";
    }

    return DomUtils.textContent(parseDocument(html));
};

export const filterLinks = (
    links?: (CustomPageLinkModel | undefined)[],
    displayChangeCustomerLink?: boolean,
    canViewClosedOrders?: boolean,
    isPunchoutSession?: boolean,
    canViewAccountSummaryAndMakePayemnts?: boolean,
): CustomPageLinkModel[] => {
    return (
        links?.filter((link): link is CustomPageLinkModel => {
            if (!link) return false;

            if (isPunchoutSession) {
                const allowedTypes = ["MyListsPage", "RfqMyQuotesPage"];
                return allowedTypes.includes(link.type || "");
            }

            const isChangeCustomerBlocked = link.type === "ChangeCustomerPage" && !displayChangeCustomerLink;

            const isOrderHistory =
                link.type === "OrderHistoryPage" && link.queryString === `?${OrderStatusQueryString.closedOrders}=true`;
            const isOrderHistoryBlocked = isOrderHistory && !canViewClosedOrders;

            const isAccountSummaryAndMakePayemntsBlocked =
                (link.isBillTrustLink || link.url.includes("MyAccount/AccountSummary")) &&
                !canViewAccountSummaryAndMakePayemnts;
            return !(isChangeCustomerBlocked || isOrderHistoryBlocked || isAccountSummaryAndMakePayemntsBlocked);
        }) || []
    );
};

// XNG-Changes: checks if all required visible fields have a value
export const allAddressFieldsGotValue = (
    address: ShipToModel | BillToModel,
    fieldDisplay: AddressFieldDisplayCollectionModel,
): boolean => {
    if (!fieldDisplay || !address) return false;
    const { validation } = address;
    if (!validation) {
        return false;
    }

    const fields = [
        "firstName",
        "lastName",
        "companyName",
        "attention",
        "address1",
        "address2",
        "address3",
        "address4",
        "country",
        "city",
        "postalCode",
        "phone",
        "fax",
        "email",
    ];

    const requiredFieldsFilled = fields.every(field => {
        const skip =
            !(validation as any)?.[field]?.isRequired ||
            !(fieldDisplay as any)?.[field]?.isVisible ||
            (validation as any)?.[field]?.isDisabled;

        return skip || !!String((address as any)[field] ?? "").trim();
    });

    const stateIsFilled = !!(address as any).state?.id;

    return requiredFieldsFilled && stateIsFilled;
};

// <XNG-Changes> - getDisabledDate from classic
export const isPastCutoff = (timeStr: string): boolean => {
    if (!timeStr) return false;
    const isPm = timeStr.toLowerCase().endsWith("pm");
    const isAm = timeStr.toLowerCase().endsWith("am");
    const parts = timeStr.replace(/am|pm/i, "").split(":").map(Number);
    let [hours, minutes] = parts;
    if (isPm && hours !== 12) hours += 12;
    if (isAm && hours === 12) hours -= 12;
    const now = new Date();
    return now.getHours() > hours || (now.getHours() === hours && now.getMinutes() >= minutes);
};

export const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const getDisabledDateTile = (customSettings: Record<string, string>, isShip: boolean) => {
    const disabledDates: Date[] = [];

    const disableWeekends = isShip
        ? customSettings.disableWeekendsForShip?.toLowerCase() === "true"
        : customSettings.disableWeekendsForPickUp?.toLowerCase() === "true";

    const disableHolidays = isShip
        ? customSettings.disableHolidaysForShip?.toLowerCase() === "true"
        : customSettings.disableHolidaysForPickUp?.toLowerCase() === "true";

    const holidays = isShip ? customSettings.shipHolidays : customSettings.pickupHolidays;

    if (disableHolidays && holidays) {
        holidays.split(",").forEach(d => disabledDates.push(new Date(d.trim())));
    }

    if (isShip) {
        if (customSettings.disableSameDayForShip?.toLowerCase() === "true") {
            disabledDates.push(new Date());
        }

        if (
            customSettings.disableNextDayForShip?.toLowerCase() === "true" &&
            isPastCutoff(customSettings.timeToDisableNextDayDelivery)
        ) {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            disabledDates.push(tomorrow);
        }
    } else if (
        customSettings.disableSameDayForPickUp?.toLowerCase() === "true" &&
        isPastCutoff(customSettings.timeToDisableSameDayDelivery)
    ) {
        disabledDates.push(new Date());
    }

    return ({ date }: { date: Date }) => {
        if (disableWeekends && [0, 6].includes(date.getDay())) return true;
        return disabledDates.some(d => isSameDay(d, date));
    };
};
// </XNG-Changes>

export const getImageSize = (image: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
        try {
            const img = new Image();

            img.onload = () => {
                resolve({ width: img.width, height: img.height });
            };

            img.src = image;
        } catch (e) {
            reject(e);
        }
    });
};

export const getSiteMessageOrEmpty = (messageName: string): string => {
    const message = siteMessage(messageName);
    return message?.toString() === messageName ? "" : (message?.toString() ?? "");
};
